import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../AuthContext';
import '../Dashboard.css';
import '../../components/MealTile.css';
import welcomePhoto from '../../assets/welcomePhoto.png';
import MealCardList from '../../components/MealCardList';

const MealsAttended = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [attendedMeals, setAttendedMeals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAttendedMeals = async () => {
      if (!currentUser) return;

      try {
        const bookingsQuery = query(
          collection(db, 'bookings'),
          where('guestId', '==', currentUser.uid),
          where('userAttendedMeal', '==', true)
        );
        const bookingsSnapshot = await getDocs(bookingsQuery);
        const bookings = bookingsSnapshot.docs.map(doc => ({
          bookingId: doc.id,
          ...doc.data()
        }));

        if (bookings.length === 0) {
          setAttendedMeals([]);
          setLoading(false);
          return;
        }

        // Fetch existing reviews by this user (as guest)
        const reviewsQuery = query(
          collection(db, 'reviews'),
          where('guestId', '==', currentUser.uid),
          where('reviewerRole', '==', 'guest')
        );
        const reviewsSnapshot = await getDocs(reviewsQuery);
        const reviewedBookingIds = new Set(
          reviewsSnapshot.docs.map(doc => doc.data().bookingId)
        );

        const meals = await Promise.all(
          bookings.map(async (booking) => {
            const mealDoc = await getDoc(doc(db, 'meals', booking.mealId));
            if (mealDoc.exists()) {
              return {
                id: mealDoc.id,
                bookingId: booking.bookingId,
                hasReviewed: reviewedBookingIds.has(booking.bookingId),
                ...mealDoc.data()
              };
            }
            return null;
          })
        );

        setAttendedMeals(meals.filter(Boolean));
      } catch (err) {
        console.error('Error fetching attended meals:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAttendedMeals();
  }, [currentUser]);

  if (!currentUser || loading) {
    return (
      <div className="dashboard-container">
        <p className="dashboard-loading">Loading...</p>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <img
        src={welcomePhoto}
        alt="Welcome"
        className="dashboard-image"
      />

      <div className="dashboard-card">
        <div className="meals-tile">
          <h2 className="meals-tile-title">Meals Attended</h2>

          <MealCardList
            meals={attendedMeals}
            onSelect={(meal) => navigate(`/meal-summary/${meal.id}`)}
            renderFooter={(meal) => (
              <button
                className={`review-link-button ${meal.hasReviewed ? 'reviewed' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!meal.hasReviewed) {
                    navigate(`/create-review/${meal.bookingId}`);
                  }
                }}
                disabled={meal.hasReviewed}
              >
                {meal.hasReviewed ? 'Reviewed' : 'Leave Review'}
              </button>
            )}
          >
            <div className="meals-empty-state">
              <p className="meals-empty">You haven't attended any meals yet.</p>
              <div className="meals-empty-actions">
                <button className="create-listing-button" onClick={() => navigate('/feed')}>
                  Browse Meals
                </button>
                <button className="create-listing-button secondary" onClick={() => navigate('/reservations')}>
                  Check Out My Reservations
                </button>
              </div>
            </div>
          </MealCardList>
        </div>

        {attendedMeals.length > 0 && (
          <button className="create-listing-button" onClick={() => navigate('/feed')}>
            Find More Meals
          </button>
        )}
        <button className="create-listing-button secondary" onClick={() => navigate('/reviews')}>
          See Reviews
        </button>
      </div>
    </div>
  );
};

export default MealsAttended;
